import { CCTData, CCTRow, CCTColumn, ColorBucketIndex, ColorBucketConfig } from './cct-types';
import { useMemo } from 'react';

// Data shaping utilities
export function useCCTDataShaping(data: CCTData) {
  return useMemo(() => {
    const { factors, measures, combinations, values } = data;

    // Create row model
    const rows: CCTRow[] = combinations.map((combo, rowIndex) => {
      const row: CCTRow = {
        id: `row-${rowIndex}`,
      };

      // Add factor values dynamically
      factors.forEach((factor, factorIndex) => {
        row[`factor${factorIndex}`] = combo[factorIndex] || '';
      });

      // Add measure values
      measures.forEach((measure, measureIndex) => {
        const value = values[rowIndex]?.[measureIndex] ?? null;
        row[measure.id] = value;
      });

      return row;
    });

    // Create column model
    const columns: CCTColumn[] = [
      // Factor columns (dynamic)
      ...factors.map((factor, index) => ({
        id: `factor${index}`,
        accessorKey: `factor${index}`,
        header: factor || `Factor ${index + 1}`,
        size: 120,
      })),
      // Measure columns
      ...measures.map((measure, index) => ({
        id: measure.id,
        accessorKey: measure.id,
        header: measure.label,
        size: 100,
      })),
    ];

    return { rows, columns };
  }, [data]);
}

// Color precomputation utilities
export function useColorPrecomputation(
  data: CCTData,
  scope: 'global' | 'per-measure' = 'per-measure'
) {
  return useMemo(() => {
    const { measures, values } = data;

    if (scope === 'per-measure') {
      // Per-measure maxima
      const measureMaxima = measures.map((measure, measureIndex) => {
        const columnValues = values
          .map(row => row[measureIndex])
          .filter(v => v !== null && typeof v === 'number') as number[];
        return columnValues.length > 0 ? Math.max(...columnValues) : 1;
      });

      return { measureMaxima, globalMax: 0 };
    } else {
      // Global maximum
      const allValues = values.flat().filter(v => v !== null && typeof v === 'number') as number[];
      const globalMax = allValues.length > 0 ? Math.max(...allValues) : 1;
      return { measureMaxima: [], globalMax };
    }
  }, [data, scope]);
}

// Color bucket calculation
export function valueToBucketIndex(
  value: number | null,
  maxValue: number,
  buckets: number = 9
): ColorBucketIndex {
  if (value === null || maxValue <= 0) return 0;

  const normalized = value / maxValue;
  const bucket = Math.floor(normalized * (buckets - 1));
  return Math.min(Math.max(bucket, 0), buckets - 1) as ColorBucketIndex;
}

// Get CSS class for color bucket
export function getColorBucketClass(bucketIndex: ColorBucketIndex): string {
  return `cct-band-${bucketIndex}`;
}

// Full cell coloring logic matching original CCTView
export function getCellColor(
  rowIndex: number,
  measureIndex: number,
  value: number | null,
  appearance: any,
  measures: any[],
  values: (number | null)[][]
): string {
  if (value === null) return appearance.valueColor || '#000000';

  const measure = measures[measureIndex];
  const measureId = measure.id || `${(measure as any)?.fieldId}_${(measure as any)?.aggregation}` || measureIndex.toString();

  // Try multiple possible keys for measure coloring
  const possibleKeys = [
    measureId.replace(':', '_'), // Replace colon with underscore (primary)
    measureId, // Original with colon
    measureIndex.toString(), // Use index as string
  ];

  // Check if per-measure coloring is enabled
  let measureColoring = null;
  for (const key of possibleKeys) {
    if (appearance.cctMeasureColoring?.[key]) {
      measureColoring = appearance.cctMeasureColoring[key];
      break;
    }
  }

  if (appearance.cctColoringScope === 'per-measure' && measureColoring) {
    if (measureColoring.mode === 'solid') {
      return measureColoring.color || appearance.valueColor || '#000000';
    } else if (measureColoring.mode === 'heatmap' && typeof value === 'number') {
      // Heatmap coloring per measure
      const columnValues = values.map(row => row[measureIndex]).filter(v => v !== null && typeof v === 'number') as number[];
      const maxValue = columnValues.length > 0 ? Math.max(...columnValues) : 1;
      const intensity = maxValue > 0 ? value / maxValue : 0;
      // Map "purples" to "purple" since that's the available palette
      const paletteMap: Record<string, string> = {
        'purples': 'purple',
        'reds': 'red',
        'blues': 'blue',
        'greens': 'green',
        'teals': 'teal',
      };
      const mappedPalette = (measureColoring.palette && paletteMap[measureColoring.palette]) || (measureColoring.palette as string) || 'blue';
      return getSequentialColor(intensity, 1, mappedPalette);
    } else if (measureColoring.mode === 'conditions' && typeof value === 'number') {
      // Condition-based coloring per measure
      const conditions = measureColoring.conditions || [];
      for (const condition of conditions) {
        let matches = false;
        switch (condition.operator) {
          case 'equals':
            matches = value === condition.value;
            break;
          case 'not_equals':
            matches = value !== condition.value;
            break;
          case 'greater_than':
            matches = value > condition.value;
            break;
          case 'greater_or_equal':
            matches = value >= condition.value;
            break;
          case 'less_than':
            matches = value < condition.value;
            break;
          case 'less_or_equal':
            matches = value <= condition.value;
            break;
          case 'between':
            matches = value >= condition.value && value <= (condition.value2 || condition.value);
            break;
        }
        if (matches) {
          return condition.color;
        }
      }
      // No condition matched, fallback to measure color
      return measureColoring.color || appearance.valueColor || '#000000';
    }
  }

  // Fallback to global coloring modes
  if (appearance.crosstabColoringMode === 'solid') {
    if (appearance.cctColoringScope === 'per-measure') {
      // Per-measure coloring: use different color per column
      return getSequentialColor(
        measureIndex / Math.max(measures.length - 1, 1),
        1,
        appearance.presetCategoricalPaletteId || 'blue'
      );
    } else {
      // Global coloring: same color for all
      return appearance.valueColor || '#000000';
    }
  } else if (appearance.crosstabColoringMode === 'heatmap' && typeof value === 'number') {
    // Heatmap coloring
    let maxValue = 1;
    if (appearance.cctColoringScope === 'per-measure') {
      // Max per measure column
      const columnValues = values.map(row => row[measureIndex]).filter(v => v !== null && typeof v === 'number') as number[];
      maxValue = columnValues.length > 0 ? Math.max(...columnValues) : 1;
    } else {
      // Global max across all measure cells
      const allValues = values.flat().filter(v => v !== null && typeof v === 'number') as number[];
      maxValue = allValues.length > 0 ? Math.max(...allValues) : 1;
    }
    const intensity = maxValue > 0 ? value / maxValue : 0;
    return getSequentialColor(intensity, 1, appearance.presetSequentialPaletteId || 'blue');
  } else if (appearance.crosstabColoringMode === 'conditions' && typeof value === 'number') {
    // Condition-based coloring - first matching rule wins
    const conditions = appearance.crosstabConditions || [];
    for (const condition of conditions) {
      let matches = false;
      switch (condition.operator) {
        case 'equals':
          matches = value === condition.value;
          break;
        case 'not_equals':
          matches = value !== condition.value;
          break;
        case 'greater_than':
          matches = value > condition.value;
          break;
        case 'greater_or_equal':
          matches = value >= condition.value;
          break;
        case 'less_than':
          matches = value < condition.value;
          break;
        case 'less_or_equal':
          matches = value <= condition.value;
          break;
        case 'between':
          matches = value >= condition.value && value <= (condition.value2 || condition.value);
          break;
      }
      if (matches) {
        return condition.color;
      }
    }
    // No condition matched, fallback to default
    return appearance.valueColor || '#000000';
  }
  return appearance.valueColor || '#000000';
}

// Import getSequentialColor for compatibility
function getSequentialColor(intensity: number, maxIntensity: number, palette: string): string {
  // This is a simplified version - in the real app this would come from the palettes utility
  const paletteColors: Record<string, string[]> = {
    'blue': ['#e3f2fd', '#bbdefb', '#90caf9', '#64b5f6', '#42a5f5', '#2196f3', '#1e88e5', '#1976d2', '#1565c0'],
    'red': ['#ffebee', '#ffcdd2', '#ef9a9a', '#e57373', '#ef5350', '#f44336', '#e53935', '#d32f2f', '#c62828'],
    'green': ['#e8f5e8', '#c8e6c9', '#a5d6a7', '#81c784', '#66bb6a', '#4caf50', '#43a047', '#388e3c', '#2e7d32'],
    'purple': ['#f3e5f5', '#e1bee7', '#ce93d8', '#ba68c8', '#ab47bc', '#9c27b0', '#8e24aa', '#7b1fa2', '#6a1b9a'],
    'teal': ['#e0f2f1', '#b2dfdb', '#80cbc4', '#4db6ac', '#26a69a', '#009688', '#00897b', '#00796b', '#00695c'],
  };

  const colors = paletteColors[palette] || paletteColors['blue'];
  const bucketIndex = Math.floor(intensity * (colors.length - 1));
  return colors[Math.min(Math.max(bucketIndex, 0), colors.length - 1)];
}

// Default appearance values
export const CCT_DEFAULTS = {
  virtualizationEnabled: true,
  rowHeight: 34,
  rowOverscan: 6,
  columnOverscan: 2,
  stickyHeader: true,
  stickyFactorColumns: 2,
} as const;
